// eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
import { useStore } from "../store";
import useClientMetrics from "./useClientMetrics";
import { useCallback, useEffect } from "react";
import { isMetaLandingOfferToken } from "../utils/marketing-url-params";
import { dataUtils, pricingUtils } from "@flights/web-api-utils-universal";
import { UIFlightData, UIPriceBreakdown } from "@flights/types";
import { flightsSessionStore } from "@flights/web-api-utils-universal";
import { PRICE_DISCREPANCY_LOGGED_OFFER } from "../constants";

const useReportPriceDiscrepancy = (flightToken: string): void => {
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();
  const { trackClientMetric } = useClientMetrics();

  const ACCEPTED_PRICE_DIFFERENCE_THRESHOLD = 5;

  const logPriceDiscrepancy = useCallback(
    (baseAPI: "FAP" | "FORAP", basePrice: UIPriceBreakdown, targetPrice: UIPriceBreakdown) => {
      if (pricingUtils.arePricesDifferent(basePrice.total, targetPrice.total)) {
        const percentageDifference = dataUtils.percentageDiff(
          pricingUtils.priceToNumber(basePrice.total),
          pricingUtils.priceToNumber(targetPrice.total)
        );

        if (baseAPI == "FORAP") {
          //also log metrics
          trackClientMetric("price_mismatch_forap_gapd");
          trackClientMetric("price_mismatch_forap_gapd_difference", Math.abs(percentageDifference));
          if (percentageDifference > ACCEPTED_PRICE_DIFFERENCE_THRESHOLD) {
            trackClientMetric("price_mismatch_forap_gapd_increase_above_5_percent");
          }
          if (percentageDifference < ACCEPTED_PRICE_DIFFERENCE_THRESHOLD * -1) {
            trackClientMetric("price_mismatch_forap_gapd_decrease_below_5_percent");
          }
        }
      }
    },
    [trackClientMetric]
  );

  //logs different type of discrepancy, ancillaries, total price...
  const logPriceDiscrepancyTypes = useCallback(
    (baseFlight: UIFlightData, targetFlight: UIFlightData, baseFlightType: "FORAP" | "FAP") => {
      if (!baseFlight || !targetFlight) {
        return;
      }
      const baseFlightTotalPrice = baseFlight.priceBreakdown;
      const targetFlightTotalPrice = targetFlight.priceBreakdown;

      const baseFlightCabinBaggagePrice = baseFlight.ancillaries?.cabinBaggagePerTraveller?.priceBreakdown;
      const baseFlightCheckedInBaggagePrice = baseFlight.ancillaries?.checkedInBaggage?.options[0]?.priceBreakdown;

      const targetFlightCabinBaggagePrice = targetFlight.ancillaries?.cabinBaggagePerTraveller?.priceBreakdown;
      const targetFlightCheckedInBaggagePrice = targetFlight.ancillaries?.checkedInBaggage?.options[0]?.priceBreakdown;

      baseFlightTotalPrice &&
        targetFlightTotalPrice &&
        logPriceDiscrepancy(baseFlightType, baseFlightTotalPrice, targetFlightTotalPrice);

      baseFlightCabinBaggagePrice &&
        targetFlightCabinBaggagePrice &&
        logPriceDiscrepancy(baseFlightType, baseFlightCabinBaggagePrice, targetFlightCabinBaggagePrice);

      baseFlightCheckedInBaggagePrice &&
        targetFlightCheckedInBaggagePrice &&
        logPriceDiscrepancy(baseFlightType, baseFlightCheckedInBaggagePrice, targetFlightCheckedInBaggagePrice);
    },
    [logPriceDiscrepancy]
  );

  //Logs price discrepancy betweeen FAP (findAirProducts) and GAPD (getAirProductDetails) APIs
  useEffect(() => {
    //log only once per offer if the changes are visible/available to the user
    const savedOffers = flightsSessionStore.get(PRICE_DISCREPANCY_LOGGED_OFFER) || {};

    if (
      !savedOffers[flightToken] &&
      store.searchResults.fetchStatus === "success" &&
      store.flightDetails.fetchStatus === "success"
    ) {
      const flightFromFAP = store.searchResults.flights.find((flight) => flight.token == flightToken);
      const flightFromGAPD = store.flightDetails;

      if (flightFromFAP && flightFromGAPD?.flight) {
        logPriceDiscrepancyTypes(flightFromFAP, flightFromGAPD.flight, "FAP");

        savedOffers[flightToken] = 1;

        flightsSessionStore.set(PRICE_DISCREPANCY_LOGGED_OFFER, savedOffers);
      }
    }
  }, [flightToken, store.flightDetails, store.searchResults, logPriceDiscrepancyTypes]);

  //Logs price discrepancy betweeen FORAP (flightFromOfferRelatedAirProducts) and GAPD (getAirProductDetails) APIs
  useEffect(() => {
    const savedOffers = flightsSessionStore.get(PRICE_DISCREPANCY_LOGGED_OFFER) || {};
    //log only once per offer if the changes are visible/available to the user
    if (
      !savedOffers[flightToken] &&
      store.flightDetails.fetchStatus === "success" &&
      store.metaFlightDetails.fetchStatus === "success" &&
      isMetaLandingOfferToken(flightToken)
    ) {
      const flightFromFORAP = store.metaFlightDetails.flight;
      const flightFromGAPD = store.flightDetails.flight;

      if (flightFromFORAP && flightFromGAPD) {
        logPriceDiscrepancyTypes(flightFromFORAP, flightFromGAPD, "FORAP");
      }
      savedOffers[flightToken] = 1;

      flightsSessionStore.set(PRICE_DISCREPANCY_LOGGED_OFFER, savedOffers);
    }
  }, [store.flightDetails, store.metaFlightDetails, flightToken, logPriceDiscrepancyTypes]);
};

export default useReportPriceDiscrepancy;
